import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useParams } from "react-router-dom";
import { useTran } from "@/hooks";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useSurveyResponseDetailStore } from "../hooks";

const formatAnswer = (answer) => {
  if (Array.isArray(answer)) return answer.join(", ") || "—";
  if (answer === null || answer === undefined || answer === "") return "—";
  if (typeof answer === "boolean") return answer ? "Yes" : "No";
  if (typeof answer === "object") return JSON.stringify(answer);
  return String(answer);
};

const SurveyResponseDetail = () => {
  const { t } = useTran();
  const { responseId } = useParams();
  const detailStore = useSurveyResponseDetailStore();

  useEffect(() => {
    detailStore.load(responseId, t);
  }, [detailStore, responseId, t]);

  const {
    detail,
    loading,
    error,
    survey,
    response,
    respondentCompany,
    derivedStats,
    groupedAnswers,
  } = detailStore;

  if (loading) {
    return <Skeleton className="h-48 w-full" />;
  }

  if (error) {
    return <p className="text-sm text-destructive">{error}</p>;
  }

  if (!detail) {
    return (
      <Card className="p-6 text-sm text-muted-foreground ">
        {t("Response not found or you do not have access.")}
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-nxstart-blue to-nxstart-green shadow-lg p-5">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <p className="text-sm uppercase tracking-wide text-white">
              {response?.surveyType === "CustomSurvey"
                ? t("Custom Survey")
                : t("Standard Survey")}
            </p>
            <h2 className="text-xl font-semibold text-white">
              {survey?.title || t("Survey")}
            </h2>
            {survey?.description ? (
              <p className="mt-1 text-sm text-white">{survey.description}</p>
            ) : null}
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold text-white">
              {derivedStats.totalScore}
            </p>
            <p className="text-xs text-white">
              {t("of")} {derivedStats.maxPossibleScore ?? "—"} {t("Points")}
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-slate-200/70 bg-white/70 p-4 text-center dark:border-slate-800 dark:bg-[#283655]">
            <p className="text-xs uppercase text-slate-500 dark:text-slate-400">
              {t("Questions")}
            </p>
            <p className="text-xl font-semibold text-slate-600 dark:text-white">
              {derivedStats.answeredQuestions || 0}/
              {derivedStats.totalQuestions || 0}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200/70 bg-white/70 p-4 text-center dark:border-slate-800 dark:bg-[#283655]">
            <p className="text-xs uppercase text-slate-500 dark:text-slate-400">
              {t("Completion Time")}
            </p>
            <p className="text-xl font-semibold text-slate-600 dark:text-white">
              {response?.completionTimeInSeconds
                ? `${Math.max(
                    1,
                    Math.round(response.completionTimeInSeconds / 60),
                  )} min`
                : "—"}
            </p>
          </div>

          <div className="rounded-lg border border-slate-200/70 bg-white/70 p-4 text-center dark:border-slate-800 dark:bg-[#283655]">
            <p className="text-xs uppercase text-slate-500 dark:text-slate-400">
              {t("Submitted")}
            </p>
            <p className="text-xl font-semibold text-slate-600 dark:text-white">
              {response?.submittedAt
                ? new Date(response.submittedAt).toLocaleDateString()
                : "—"}
            </p>
            {response?.submittedAt && (
              <p className="text-xl font-semibold text-slate-600 dark:text-white">
                {new Date(response.submittedAt).toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200/70 bg-white/70 p-4 dark:border-slate-800 dark:bg-[#283655]">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase text-slate-500 dark:text-slate-400">
                Respondent
              </p>
              <span
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  response?.isExternalRespondent
                    ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-200"
                    : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-200"
                }`}
              >
                {response?.isExternalRespondent ? "External" : "Internal"}
              </span>
            </div>
            <p className="mt-2 text-base font-semibold text-slate-700 dark:text-white">
              {response?.respondent?.name ||
                response?.externalRespondent?.name ||
                "—"}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              {response?.respondent?.email ||
                response?.externalRespondent?.email ||
                "No email"}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              {respondentCompany?.companyName ||
                response?.externalRespondent?.organization ||
                "No organization"}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {respondentCompany?.companyName ? (
            <Badge className="bg-white/20 text-white border border-white/30 hover:bg-white/30">
              {respondentCompany.companyName}
            </Badge>
          ) : null}
          {response?.respondent ? (
            <Badge className="bg-white/20 text-white border border-white/30 hover:bg-white/30">
              {response?.respondent?.email ||
                response?.externalRespondent?.email ||
                "No email"}
            </Badge>
          ) : null}
        </div>
      </Card>
      <div className="grid gap-4 md:grid-cols-2">
        {Object.keys(groupedAnswers).map((category) => (
          <Card
            key={category}
            className="border border-slate-200/70 bg-white/80 p-3 shadow-sm dark:border-slate-800 dark:bg-[#283655]"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold capitalize text-slate-900 dark:text-white">
                {category}
              </p>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {groupedAnswers[category].length} questions
              </span>
            </div>
            <div className="space-y-2">
              {groupedAnswers[category].map((ans) => (
                <div
                  key={ans.questionId}
                  className="rounded-lg border border-slate-200 bg-white/80 p-3 dark:border-slate-700 dark:bg-[#283655]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {ans.questionText}
                        </p>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Answer: {formatAnswer(ans.answer)}
                      </p>
                      {ans.sampleAnswer && (
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Ideal: {ans.sampleAnswer}
                        </p>
                      )}
                    </div>
                    {["single_choice", "multiple_choice"].includes(
                      ans.questionType,
                    ) && (
                      <div className="text-right whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                        <p className="font-semibold text-slate-700 dark:text-slate-200">
                          {ans.awardedPoints ?? ans.pointsAwarded ?? 0} /{" "}
                          {ans.maxPoints ?? 0} Points
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default observer(SurveyResponseDetail);
